import React, { useEffect, useState } from "react";
import {
  Page,
  Card,
  Text,
  Box,
  Button,
  InlineStack,
  BlockStack,
  Banner,
  ProgressBar,
  Modal
} from "@shopify/polaris";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const USER_MESSAGES = {
  noSuggestions:
    "We couldn’t find title ideas yet. Please regenerate ideas to continue.",
  fetchFailed:
    "We couldn’t fetch suggestions right now. Please try again.",
  regenerateFailed:
    "We couldn’t regenerate ideas right now. Please try again.",
  sessionGenericExpired:
    "Your session data expired. Please go back and enter your keywords again.",
  sessionProductExpired:
    "Your session data expired. Please go back and enter your product details again.",
  generateFailed:
    "We couldn’t generate the article right now. Please try again.",
  billingInvalidUrl:
    "We couldn’t start billing because the confirmation link was invalid. Please try again.",
  billingActivateFailed:
    "We couldn’t activate billing right now. Please try again.",
  billingNoUrl:
    "We couldn’t get billing confirmation. Please try again.",
};

function RegenerateTitleCombos({ apiKey, shopOrigin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [titleTeasers, setTitleTeasers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Progress bar states for content generation
  const [progress, setProgress] = useState(0);
  const [progressVisible, setProgressVisible] = useState(false);
  const [contentGenerated, setContentGenerated] = useState(false);
  const [generatingIndex, setGeneratingIndex] = useState(null);

  // Progress bar states for idea regeneration
  const [regenerateProgress, setRegenerateProgress] = useState(0);
  const [regenerateProgressVisible, setRegenerateProgressVisible] = useState(false);
  // const [showLimitModal, setShowLimitModal] = useState(false);
  // const [pendingArticle, setPendingArticle] = useState(null); // Store article before proceeding


  // const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  // const [subscriptionLoading, setSubscriptionLoading] = useState(true); // Add this

  // const [isInitialActivation, setIsInitialActivation] = useState(false);

  // Update the useEffect
  // useEffect(() => {
  //   const fetchSubscription = async () => {
  //     setSubscriptionLoading(true); // Set loading to true
  //     try {
  //       const response = await axios.get(
  //         `/get_subscription/?shop_name=${encodeURIComponent(shopOrigin || "")}`
  //       );
  //       if (response.data.plan) {
  //         setSubscriptionPlan(response.data.plan); // "free", "basic", "pro"
  //       } else {
  //         setSubscriptionPlan(null);
  //       }
  //     } catch (err) {
  //       console.error("Error fetching subscription:", err);
  //       setSubscriptionPlan(null);
  //     } finally {
  //       setSubscriptionLoading(false); // Set loading to false when done
  //     }
  //   };

  //   if (shopOrigin) { // Only fetch if shopOrigin exists
  //     fetchSubscription();
  //   }
  // }, [shopOrigin]);


  // Progress bar function for content generation
  function startProgressBar(onComplete) {
    setProgressVisible(true);
    let progressValue = 0;
    const animatedDuration = 30000; // 30 seconds
    const intervalTime = 50;
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      progressValue = Math.min(98, (elapsedTime / animatedDuration) * 98);
      setProgress(progressValue);

      if (progressValue >= 98 || elapsedTime >= animatedDuration) {
        clearInterval(interval);
        setProgress(98);
      }
    }, intervalTime);

    return () => {
      clearInterval(interval);
      setProgress(100);
      setTimeout(() => {
        setProgressVisible(false);
        onComplete && onComplete();
      }, 500);
    };
  }

  // Progress bar function for idea regeneration
  function startRegenerateProgressBar(onComplete) {
    setRegenerateProgressVisible(true);
    let progressValue = 0;
    const animatedDuration = 10000; // 10 seconds for regeneration
    const intervalTime = 50;
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      progressValue = Math.min(98, (elapsedTime / animatedDuration) * 98);
      setRegenerateProgress(progressValue);

      if (progressValue >= 98 || elapsedTime >= animatedDuration) {
        clearInterval(interval);
        setRegenerateProgress(98);
      }
    }, intervalTime);

    return () => {
      clearInterval(interval);
      setRegenerateProgress(100);
      setTimeout(() => {
        setRegenerateProgressVisible(false);
        onComplete && onComplete();
      }, 500);
    };
  }

  useEffect(() => {
    const fetchTitleTeasers = async () => {
      setLoading(true);
      setError(null);

      try {
        // Check if returning from billing activation
        // const urlParams = new URLSearchParams(window.location.search);
        // const billingActivated = urlParams.get('billing_activated') === 'true';
        
        // if (billingActivated) {
        //   // Retrieve pending article from localStorage
        //   const storedPendingArticle = localStorage.getItem(`pending_article_${shopOrigin}`);
        //   if (storedPendingArticle) {
        //     try {
        //       const pendingArticleData = JSON.parse(storedPendingArticle);
              
        //       // Generate the 4th blog immediately
        //       navigate(`/edit-blogs?shop=${encodeURIComponent(shopOrigin || "")}`, {
        //         state: {
        //           article: {
        //             title: pendingArticleData.article.title,
        //             descriptive: pendingArticleData.article.descriptive,
        //             readtime: pendingArticleData.article.readtime,
        //             content: pendingArticleData.article.content,
        //           },
        //           language: pendingArticleData.language,
        //         },
        //       });

        //       // Increment blog count
        //       axios.post(
        //         "/api/increment-blog-count/",
        //         new URLSearchParams({ shop_name: shopOrigin }),
        //         { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        //       ).catch(err => {
        //         console.error("Failed to increment blogs_count", err);
        //       });

        //       // Create usage record ($3 per blog post)
        //       axios.post(
        //         "/create_usage_record/",
        //         new URLSearchParams({
        //           shop_name: shopOrigin,
        //           description: "3$ per blog post",
        //           amount: "3.00"
        //         }),
        //         { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        //       ).then(res => {
        //         console.log("Usage record created:", res.data);
        //       }).catch(err => {
        //         console.error("Failed to create usage record", err.response?.data || err);
        //       });

        //       // Clean up localStorage
        //       localStorage.removeItem(`pending_article_${shopOrigin}`);
              
        //       // Clean up URL parameter
        //       window.history.replaceState({}, '', window.location.pathname + `?shop=${encodeURIComponent(shopOrigin)}`);
              
        //       return;
        //     } catch (err) {
        //       console.error("Error parsing stored pending article:", err);
        //     }
        //   }
        // }

        const { titleTeasers: stateTitleTeasers } = location.state || {};
        if (stateTitleTeasers && stateTitleTeasers.length > 0) {
          setTitleTeasers(stateTitleTeasers);
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `/api/regenerate-titles/${encodeURIComponent(shopOrigin || "")}/`,
          { headers: { "Content-Type": "application/json" } }
        );

        if (response.data.status === "success" && response.data.title_teasers) {
          setTitleTeasers(response.data.title_teasers);
        } else {
          setError(response.data.message || USER_MESSAGES.noSuggestions);
          setTitleTeasers([]);
        }
      } catch (err) {
        setError(err.response?.data?.message || USER_MESSAGES.fetchFailed);
        setTitleTeasers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTitleTeasers();
  }, [shopOrigin, location.state, navigate]);

  

  
  // const handleGenerateArticle = async (item, index) => {
  //   setError(null);
  //   setGeneratingIndex(index);

  //   // Check if subscription is still loading
  //   if (subscriptionLoading) {
  //     setError("Loading subscription details...");
  //     setGeneratingIndex(null);
  //     return;
  //   }

  //   if (!subscriptionPlan) {
  //     setError("Please subscribe to a plan to generate content.");
  //     setGeneratingIndex(null);
  //     return;
  //   }

  //   let completeProgressBar = startProgressBar();

  //   try {
  //     const cachedData = await axios.get(
  //       `/api/get-content/?shop=${encodeURIComponent(shopOrigin || "")}`,
  //       { headers: { "Content-Type": "application/json" } }
  //     );

  //     if (cachedData.data.status !== "success") {
  //       setError(cachedData.data.message || "No cached data found.");
  //       if (completeProgressBar) completeProgressBar();
  //       setGeneratingIndex(null);
  //       return;
  //     }

  //     const response = await axios.post(
  //       `/api/regenerate-content/${encodeURIComponent(shopOrigin || "")}/`,
  //       {
  //         shop: shopOrigin,
  //         tone: cachedData.data.data.tone || "friendly",
  //         language: cachedData.data.data.language || "English",
  //         content_type: cachedData.data.data.content_type || "generic_content",
  //         keywords: cachedData.data.data.keywords || "",
  //         product_url: cachedData.data.data.product_url || "",
  //         title: item.title,
  //         teaser: item.teaser,
  //       },
  //       { headers: { "Content-Type": "application/json" } }
  //     );

  //     if (response.data.status === "success" && response.data.suggestions) {
  //       const article = response.data.suggestions[0];
  //       if (completeProgressBar) completeProgressBar();
  //       setContentGenerated(true);

  //       const countRes = await axios.get(
  //         `/api/get-subscription-blog-count/?shop=${encodeURIComponent(shopOrigin || "")}`
  //       );

  //       if (countRes.data.status === "success") {
  //         const currentCount = countRes.data.blogs_count;
  //         // console.log("currentCount of blogs generated:::: ", currentCount);

  //         // ✅ Check if this is the 4th blog for free plan users
  //         if (subscriptionPlan === "free" && currentCount === 3) {
  //           // This is the 4th blog - show billing activation modal
  //           setPendingArticle({
  //             article,
  //             language: cachedData.data.data.language || "English",
  //           });
  //         setIsInitialActivation(true); // Mark as initial activation
  //         setShowLimitModal(true);
  //         return;
  //       }


  //       // ✅ For 5th, 6th, 7th... blogs (after initial activation)
  //       if (subscriptionPlan === "free" && currentCount > 3) {
  //         // Check if billing is activated
  //         try {
  //           const subRes = await axios.get(
  //             `/get_subscription/?shop_name=${encodeURIComponent(shopOrigin || "")}`
  //           );
            
  //           // If charge_id is 11111 (dummy) or doesn't exist, billing isn't activated
  //           if (!subRes.data.charge_id || subRes.data.charge_id === '11111' || subRes.data.charge_id === 11111) {
  //             // Billing not activated - show modal to activate billing
  //             setPendingArticle({
  //               article,
  //               language: cachedData.data.data.language || "English",
  //             });
  //             setIsInitialActivation(true); // Still needs activation
  //             setShowLimitModal(true);
  //             return;
  //           } else {
  //             // Billing is activated - show confirmation modal for $3 charge
  //             setPendingArticle({
  //               article,
  //               language: cachedData.data.data.language || "English",
  //             });
  //             setIsInitialActivation(false); // Not initial activation, just confirmation
  //             setShowLimitModal(true);
  //             return;
  //           }
  //         } catch (subErr) {
  //           console.warn("Could not verify billing status, proceeding with caution:", subErr);
  //           setPendingArticle({
  //             article,
  //             language: cachedData.data.data.language || "English",
  //           });
  //           setIsInitialActivation(true); // Safe default
  //           setShowLimitModal(true);
  //           return;
  //         }
  //       }
          
  //         // ✅ Also check if user has blogs > 3 but billing hasn't been activated yet
  //         // This can happen after app reinstall - user might have blogs_count > 3 but charge_id = 11111
  //         if (subscriptionPlan === "free" && currentCount > 3) {
  //           // Check if billing is activated by verifying subscription has valid charge_id
  //           try {
  //             const subRes = await axios.get(
  //               `/get_subscription/?shop_name=${encodeURIComponent(shopOrigin || "")}`
  //             );
              
  //             // If charge_id is 11111 (dummy) or doesn't exist, billing isn't activated
  //             if (!subRes.data.charge_id || subRes.data.charge_id === '11111' || subRes.data.charge_id === 11111) {
  //               // Billing not activated - show modal to activate billing
  //               setPendingArticle({
  //                 article,
  //                 language: cachedData.data.data.language || "English",
  //               });
  //               setShowLimitModal(true);
  //               return;
  //             }
  //           } catch (subErr) {
  //             console.warn("Could not verify billing status, proceeding with caution:", subErr);
  //             // If we can't verify, show modal to be safe
  //             setPendingArticle({
  //               article,
  //               language: cachedData.data.data.language || "English",
  //             });
  //             setShowLimitModal(true);
  //             return;
  //           }
  //         }

  //         // ✅ Different thresholds for different plans (for basic plan)
  //         let limit = 0;
  //         if (subscriptionPlan === "basic") {
  //           limit = 35; // After 35, show modal
  //         } else if (subscriptionPlan === "pro") {
  //           limit = Infinity; // No limit for pro
  //         }
          
  //         if (Number.isFinite(limit) && limit > 0 && currentCount + 1 > limit) {
  //           setPendingArticle({
  //             article,
  //             language: cachedData.data.data.language || "English",
  //           });
  //           setShowLimitModal(true);
  //           return;
  //         }

  //         // ✅ Proceed directly if under limit or pro plan
  //         navigate(`/edit-blogs?shop=${encodeURIComponent(shopOrigin || "")}`, {
  //           state: {
  //             article: {
  //               title: article.title,
  //               descriptive: article.descriptive,
  //               readtime: article.readtime,
  //               content: article.content,
  //             },
  //             language: cachedData.data.data.language || "English",
  //           },
  //         });

  //         // Increment count
  //         axios.post(
  //           "/api/increment-blog-count/",
  //           new URLSearchParams({ shop_name: shopOrigin }),
  //           { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
  //         ).catch(err => {
  //           console.error("Failed to increment blogs_count", err);
  //         });

  //         // Charge $3 per blog post:
  //         // - For free plan: only after 4th blog AND billing is activated
  //         // - For basic plan: only after 35th blog
  //         // - For pro plan: never charge
  //         let shouldCharge = false;
  //         if (subscriptionPlan === "free" && currentCount >= 3) {
  //           // For free plan, only charge if billing has been activated (4th blog and onwards)
  //           // If currentCount === 3, the modal should have been shown above, so this means billing is activated
  //           shouldCharge = true; // 4th blog and onwards (billing should be activated)
  //         } else if (subscriptionPlan === "basic" && currentCount > 35) {
  //           shouldCharge = true; // After 35th blog
  //         }

  //         if (shouldCharge) {
  //           // Attempt to create usage record - backend will handle validation
  //           axios.post(
  //             "/create_usage_record/",
  //             new URLSearchParams({
  //               shop_name: shopOrigin,
  //               description: "3$ per blog post",
  //               amount: "3.00"
  //             }),
  //             { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
  //           ).then(res => {
  //             console.log("Usage record created:", res.data);
  //           }).catch(err => {
  //             // If billing not activated, log but don't show error to user
  //             // The backend will return appropriate error message
  //             if (err.response?.data?.message?.includes('billing not activated')) {
  //               console.warn("Billing not activated yet, skipping usage record:", err.response?.data?.message);
  //             } else {
  //               console.error("Failed to create usage record", err.response?.data || err);
  //             }
  //           });
  //         }
  //       }
  //     }
  //   } catch (err) {
  //     setError(err.response?.data?.message || "Error generating article.");
  //     if (completeProgressBar) completeProgressBar();
  //     setGeneratingIndex(null);
  //   }
  // };


  const handleGenerateArticle = async (item, index) => {
    setError(null);
    setGeneratingIndex(index);

    let completeProgressBar = startProgressBar();

    try {
      // Step 1: Try to get cached content data.
      // If cache miss, we can still proceed because the frontend
      // has enough context (from location.state set by GenericContentInput).
      let contentContext = null;

      try {
        const cachedData = await axios.get(
          `/api/get-content/?shop=${encodeURIComponent(shopOrigin || "")}`,
          { headers: { "Content-Type": "application/json" } }
        );
        if (cachedData.data.status === "success") {
          contentContext = cachedData.data.data;
        }
      } catch (cacheErr) {
        console.warn(
          "Cache miss on get-content, will use location.state context:",
          cacheErr?.response?.data?.message || cacheErr.message
        );
      }

      // Step 2: Build the payload for regenerate-content.
      // Prefer cached values, fall back to location.state, then defaults.
      const stateData = location.state || {};

      // Extract keywords from location.state — GenericContentInput passes
      // titleTeasers via navigate state; the full content data is in
      // the ContentContext which was set before navigation.
      // We read keywords from contentContext first, then stateData, then
      // the URL search params as a last resort.
      const urlParams = new URLSearchParams(window.location.search);
      
      const resolvedTone = contentContext?.tone || stateData?.tone || "friendly";
      const resolvedLanguage = contentContext?.language || stateData?.language || "English";
      const resolvedContentType = contentContext?.content_type || stateData?.content_type || "generic_content";
      const resolvedKeywords = contentContext?.keywords || stateData?.keywords || "";
      const resolvedProductUrl = contentContext?.product_url || stateData?.product_url || "";

      // If we still have no keywords and content type is generic_content,
      // we cannot generate — show a clear error.
      if (resolvedContentType === "generic_content" && !resolvedKeywords.trim()) {
        setError(USER_MESSAGES.sessionGenericExpired);
        if (completeProgressBar) completeProgressBar();
        setGeneratingIndex(null);
        return;
      }
      if (
        resolvedContentType === "generic_product" &&
        !resolvedKeywords.trim() &&
        !resolvedProductUrl.trim()
      ) {
        setError(USER_MESSAGES.sessionProductExpired);
        if (completeProgressBar) completeProgressBar();
        setGeneratingIndex(null);
        return;
      }

      // Step 3: Call regenerate-content with full context in the body.
      // The backend now uses request body values preferentially over cache,
      // so this works even when the cache has expired.
      const response = await axios.post(
        `/api/regenerate-content/${encodeURIComponent(shopOrigin || "")}/`,
        {
          shop: shopOrigin,
          tone: resolvedTone,
          language: resolvedLanguage,
          content_type: resolvedContentType,
          keywords: resolvedKeywords,
          product_url: resolvedProductUrl,
          title: item.title,
          teaser: item.teaser,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.status === "success" && response.data.suggestions) {
        const article = response.data.suggestions[0];
        if (completeProgressBar) completeProgressBar();

        navigate(`/edit-blogs?shop=${encodeURIComponent(shopOrigin || "")}`, {
          state: {
            article: {
              title: article.title,
              descriptive: article.descriptive,
              readtime: article.readtime,
              content: article.content,
            },
            language: resolvedLanguage,
          },
        });
      } else {
        setError(
          response.data.message || USER_MESSAGES.generateFailed
        );
        if (completeProgressBar) completeProgressBar();
        setGeneratingIndex(null);
      }
    } catch (err) {
      console.error("Error generating article:", err.response?.data || err.message);
      setError(
        err.response?.data?.message || USER_MESSAGES.generateFailed
      );
      if (completeProgressBar) completeProgressBar();
      setGeneratingIndex(null);
    }
  };


  const handleRegenerateIdeas = async () => {
    setLoading(true);
    setError(null);
    setGeneratingIndex(null);

    let completeRegenerateProgressBar = startRegenerateProgressBar();

    try {
      const response = await axios.get(
        `/api/regenerate-titles/${encodeURIComponent(shopOrigin || "")}/`,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.status === "success" && response.data.title_teasers) {
        setTitleTeasers(response.data.title_teasers);
      } else {
        setError(response.data.message || USER_MESSAGES.noSuggestions);
        setTitleTeasers([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || USER_MESSAGES.regenerateFailed);
      setTitleTeasers([]);
    } finally {
      setLoading(false);
      if (completeRegenerateProgressBar) completeRegenerateProgressBar();
    }
  };

  return (
    <Page title={generatingIndex !== null ? "" : "Suggested Titles & Content Teasers"}>
      <Card padding={{ xs: "600", sm: "800" }} sectioned>
        {generatingIndex === null && (
          <Box paddingBlockEnd="400">
            <Text color="subdued" variant="bodyMd" as="p">
              Choose a title to generate full blog content
            </Text>
          </Box>
        )}

        {error && (
          <Banner status="critical" title="Couldn’t fetch suggestions">
            <p>{error}</p>
          </Banner>
        )}

        {!contentGenerated && !loading && !error && titleTeasers.length === 0 && (
          <Text variant="bodyMd">No title suggestions found.</Text>
        )}

        {!contentGenerated && !error && titleTeasers.length > 0 && (
          loading && regenerateProgressVisible ? (
            // Show progress bar for regenerating ideas
            <Box paddingBlock="400">
              <Text variant="bodyMd">Regenerating Idea</Text>
              <ProgressBar progress={regenerateProgress} size="small" />
            </Box>
          ) : generatingIndex !== null && progressVisible ? (
            // Show progress bar for content generation
            <Box paddingBlock="400">
              <div style={{marginBottom: "5px"}}>
                <Text variant="bodyMd">
                  Generating Content: "{titleTeasers[generatingIndex].title}"
                </Text>
              </div>
              <ProgressBar progress={progress} size="small" />
            </Box>
          ) : (
            // Show all cards when no generation or regeneration is in progress
            titleTeasers.map((item, index) => (
              <div style={{ padding: "15px" }} key={index}>
                <Card sectioned>
                  <BlockStack gap="200">
                    <Text variant="headingMd">{item.title}</Text>
                    <Text>{item.teaser}</Text>
                    <Box>
                      <Button
                        variant="primary"
                        onClick={() => handleGenerateArticle(item, index)}
                        disabled={generatingIndex !== null || loading}
                      >
                        Generate Content
                      </Button>
                    </Box>
                  </BlockStack>
                </Card>
              </div>
            ))
          )
        )}
      </Card>
      <div style={{ marginTop: "15px", marginBottom: "10px", marginRight: "15px", marginLeft: "15px" }}>
        <InlineStack align="space-between">
          <Button
            size="medium"
            onClick={() =>
              navigate(`/content-setup?shop=${encodeURIComponent(shopOrigin || "")}`)
            }
          >
            Back
          </Button>
          {generatingIndex === null && !loading && (
            <Button
              size="medium"
              variant="primary"
              onClick={handleRegenerateIdeas}
              disabled={loading}
            >
              Regenerate Idea
            </Button>
          )}
        </InlineStack>
      </div>
      {/* <Modal
        open={showLimitModal}
        onClose={() => {
          setShowLimitModal(false);
          navigate(`/content-setup?shop=${encodeURIComponent(shopOrigin || "")}`);
        }}
        title={
          subscriptionPlan === "free" && isInitialActivation
            ? "Activate Usage-Based Billing"
            : "Blog Post Limit Reached"
        }
        primaryAction={{
          content: "Yes, Proceed",
          tone: "success",
          onAction: async () => {
            // If free plan and initial activation (4th blog or billing not active)
            if (subscriptionPlan === "free" && isInitialActivation) {
              try {
                // Store pending article in localStorage before redirecting
                if (pendingArticle) {
                  localStorage.setItem(`pending_article_${shopOrigin}`, JSON.stringify(pendingArticle));
                }

                const response = await axios.post(
                  "/api/activate-usage-billing/",
                  new URLSearchParams({ shop_name: shopOrigin }),
                  { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
                );

                console.log("Billing activation response:", response.data);

                // Check if billing is already activated (no redirect needed)
                if (response.data && response.data.status === 'success') {
                  // Billing already activated, proceed with generating the blog
                  console.log("Billing already activated, proceeding with blog generation");
                  setShowLimitModal(false);
                  
                  // Generate the blog immediately
                  if (pendingArticle) {
                    navigate(`/edit-blogs?shop=${encodeURIComponent(shopOrigin || "")}`, {
                      state: {
                        article: {
                          title: pendingArticle.article.title,
                          descriptive: pendingArticle.article.descriptive,
                          readtime: pendingArticle.article.readtime,
                          content: pendingArticle.article.content,
                        },
                        language: pendingArticle.language,
                      },
                    });

                    // Increment blog count
                    axios.post(
                      "/api/increment-blog-count/",
                      new URLSearchParams({ shop_name: shopOrigin }),
                      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
                    ).catch(err => {
                      console.error("Failed to increment blogs_count", err);
                    });

                    // Create usage record ($3 per blog post)
                    axios.post(
                      "/create_usage_record/",
                      new URLSearchParams({
                        shop_name: shopOrigin,
                        description: "3$ per blog post",
                        amount: "3.00"
                      }),
                      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
                    ).then(res => {
                      console.log("Usage record created:", res.data);
                    }).catch(err => {
                      console.error("Failed to create usage record", err.response?.data || err);
                    });
                  }
                  
                  // Clear pending article from localStorage
                  localStorage.removeItem(`pending_article_${shopOrigin}`);
                  return;
                }

                if (response.data && response.data.url) {
                  // Redirect to Shopify billing approval
                  const billingUrl = response.data.url;
                  console.log("Redirecting to billing URL:", billingUrl);
                  
                  // Ensure the URL is valid and redirect
                  if (billingUrl && (billingUrl.startsWith('http://') || billingUrl.startsWith('https://'))) {
                    if (window.top) {
                      window.top.location.href = billingUrl;
                    } else {
                      window.location.href = billingUrl;
                    }
                  } else {
                    setError(USER_MESSAGES.billingInvalidUrl);
                    localStorage.removeItem(`pending_article_${shopOrigin}`);
                    setShowLimitModal(false);
                  }
                } else if (response.data && response.data.error) {
                  setError(response.data.error || USER_MESSAGES.billingActivateFailed);
                  localStorage.removeItem(`pending_article_${shopOrigin}`);
                  setShowLimitModal(false);
                } else {
                  setError(USER_MESSAGES.billingNoUrl);
                  localStorage.removeItem(`pending_article_${shopOrigin}`);
                  setShowLimitModal(false);
                }
              } catch (err) {
                console.error("Failed to activate usage billing", err.response?.data || err);
                const errorMessage =
                  err.response?.data?.error ||
                  err.response?.data?.message ||
                  USER_MESSAGES.billingActivateFailed;
                setError(errorMessage);
                localStorage.removeItem(`pending_article_${shopOrigin}`);
                setShowLimitModal(false);
              }
            } else {
              // For free plan with billing activated (5th, 6th... blogs) or basic plan
              if (pendingArticle) {
                navigate(`/edit-blogs?shop=${encodeURIComponent(shopOrigin || "")}`, {
                  state: {
                    article: {
                      title: pendingArticle.article.title,
                      descriptive: pendingArticle.article.descriptive,
                      readtime: pendingArticle.article.readtime,
                      content: pendingArticle.article.content,
                    },
                    language: pendingArticle.language,
                  },
                });

                // ✅ Increment blog count
                axios.post(
                  "/api/increment-blog-count/",
                  new URLSearchParams({ shop_name: shopOrigin }),
                  { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
                ).catch(err => {
                  console.error("Failed to increment blogs_count", err);
                });

                // ✅ Create usage record ($3 per blog post)
                axios.post(
                  "/create_usage_record/",
                  new URLSearchParams({
                    shop_name: shopOrigin,
                    description: "3$ per blog post",
                    amount: "3.00"
                  }),
                  { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
                ).then(res => {
                  console.log("Usage record created:", res.data);
                }).catch(err => {
                  console.error("Failed to create usage record", err.response?.data || err);
                });
              }
              setShowLimitModal(false);
            }
          },
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => {
              setShowLimitModal(false);
              navigate(`/content-setup?shop=${encodeURIComponent(shopOrigin || "")}`);
            },
          },
        ]}
      >
        <Modal.Section>
          {subscriptionPlan === "free" && isInitialActivation ? (
            <BlockStack gap="400">
              <Text>
                You've reached the free limit for generating blog posts. From now on, you'll be charged <strong>$3 per blog post</strong>.
              </Text>
              <Text>
                You'll only be charged after approving the plan — no hidden fees, and you can cancel anytime.
              </Text>
            </BlockStack>
          ) : (
            <Text>
              You have reached the limit for generating blog posts. From now on, you will be charged <strong>$3 per blog post.</strong> Please confirm if you wish to proceed.
            </Text>
          )}
        </Modal.Section>
      </Modal> */}
    </Page>
  );

}

export default RegenerateTitleCombos;
